import { useNavigate } from "react-router-dom";
import { GraduationCap, BookOpen, Bell, CalendarDays, ClipboardCheck, HeadphonesIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import logo from "@/assets/logo-stdavid.webp";
import DevAvatarAnimation from "@/components/auth/DevAvatarAnimation";

const PUBLISHED_URL = "https://mindsetsolutionsafrica.group";
const REGISTER_PATH = "/student-signup";

const WHATSAPP_MESSAGE = `You are invited to join St. David Student Management System. Register now and start enjoying academic and student support services.\n\n${PUBLISHED_URL}/student-signup`;
const EMAIL_SUBJECT = "Invitation to Join St. David Student Management System";
const EMAIL_BODY = `Hello,\n\nYou are invited to join St. David Student Management System.\n\nRegister now and start enjoying academic and student support services in one place.\n\n${PUBLISHED_URL}/student-signup\n\nBest regards,\nSt. David / Mindset Solutions`;

const features = [
  { icon: BookOpen, title: "Access Academic Information", desc: "View your courses, results, and academic progress anytime." },
  { icon: Bell, title: "View Announcements", desc: "Stay updated with the latest college notices and news." },
  { icon: CalendarDays, title: "Check Timetable & Updates", desc: "Never miss a class or important academic date." },
  { icon: ClipboardCheck, title: "Monitor Attendance", desc: "Track your attendance and eligibility status in real time." },
  { icon: HeadphonesIcon, title: "Manage Student Services", desc: "Submit requests and manage services online with ease." },
  { icon: GraduationCap, title: "Exam & Results", desc: "Access examination schedules and view your results securely." },
];

const InviteLanding = () => {
  const navigate = useNavigate();

  const handleRegister = () => navigate(REGISTER_PATH);

  const handleWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, "_blank");
  };

  const handleEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Hero ── */}
      <section className="flex flex-col items-center text-center px-4 pt-10 pb-8 md:pt-16 md:pb-12 gap-4">
        <div className="h-[120px] w-[120px] rounded-full bg-primary/10 flex items-center justify-center p-2 shadow-md">
          <img src={logo} alt="St. David College" className="h-full w-full object-contain" />
        </div>

        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight max-w-2xl">
          Welcome to St. David<br className="hidden sm:block" /> Student Management System
        </h1>

        <p className="text-muted-foreground text-sm md:text-base max-w-lg">
          Register now and start enjoying student services anytime, anywhere.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <Button size="lg" onClick={handleRegister} className="px-8 text-base font-semibold">
            Register Now
          </Button>
          <Button size="lg" variant="outline" onClick={handleRegister} className="px-8 text-base font-semibold">
            Start Enjoying Services
          </Button>
        </div>
      </section>

      {/* ── Value Proposition ── */}
      <section className="px-4 py-8 md:py-12 bg-muted/40">
        <h2 className="text-center text-lg md:text-xl font-bold text-foreground mb-6">
          What You Can Do
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {features.map((f) => (
            <Card key={f.title} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="flex items-start gap-3 p-5">
                <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{f.title}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{f.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Invitation Actions ── */}
      <section className="px-4 py-8 md:py-12">
        <h2 className="text-center text-lg md:text-xl font-bold text-foreground mb-2">
          Invite Others to Join
        </h2>
        <p className="text-center text-muted-foreground text-sm mb-6 max-w-md mx-auto">
          Share the platform with friends and fellow students.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button onClick={handleWhatsApp} size="lg" className="gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-6">
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Invite via WhatsApp
          </Button>
          <Button onClick={handleEmail} size="lg" variant="outline" className="gap-2 px-6">
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
            Invite via Email
          </Button>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="px-4 py-8 md:py-10 bg-primary/5">
        <div className="text-center max-w-lg mx-auto">
          <h2 className="text-lg md:text-xl font-bold text-foreground mb-2">Join St. David Today</h2>
          <p className="text-muted-foreground text-sm mb-4">
            Everything you need for your academic journey — in one place.
          </p>
          <Button size="lg" onClick={handleRegister} className="px-10 text-base font-semibold">
            Register Now
          </Button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="px-4 py-6 border-t border-border bg-muted/20">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-2">
            <img src={logo} alt="St. David College" className="h-8 w-8 object-contain" />
            <span className="text-sm font-semibold text-foreground">St. David College of Health Sciences</span>
          </div>
          <p className="text-xs text-muted-foreground max-w-sm">
            Secure, simple, and built for modern student services.
          </p>
          <div className="opacity-70 scale-[0.55]">
            <DevAvatarAnimation />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InviteLanding;
